/** Resume accent color (name + section headers in preview / PDF / Word). */

export type ResumeColorPresetId =
  | 'evergreen'
  | 'orange'
  | 'amber'
  | 'navy'
  | 'burgundy'
  | 'custom'

export interface ResumeColorPreset {
  id: Exclude<ResumeColorPresetId, 'custom'>
  label: string
  accentHex: string
}

export const RESUME_COLOR_PRESETS: ResumeColorPreset[] = [
  { id: 'evergreen', label: 'Evergreen', accentHex: '#00786c' },
  { id: 'orange', label: 'Orange', accentHex: '#f57601' },
  { id: 'amber', label: 'Amber', accentHex: '#c87c49' },
  { id: 'navy', label: 'Navy', accentHex: '#1e3a5f' },
  { id: 'burgundy', label: 'Burgundy', accentHex: '#8b2942' },
]

export const DEFAULT_RESUME_COLOR_PRESET: ResumeColorPresetId = 'orange'

const STORAGE_KEY = 'applicator-resume-color-scheme'
const STORAGE_CUSTOM_HEX_KEY = 'applicator-resume-color-custom-hex'

export interface ResumeColorPreference {
  presetId: ResumeColorPresetId
  customHex: string
}

export function loadResumeColorPreference(): ResumeColorPreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const customHex = localStorage.getItem(STORAGE_CUSTOM_HEX_KEY) || '#1e6b58'
    if (raw === 'custom' || raw === 'evergreen' || raw === 'orange' || raw === 'amber' || raw === 'navy' || raw === 'burgundy') {
      return { presetId: raw, customHex }
    }
  } catch {
    /* ignore */
  }
  return { presetId: DEFAULT_RESUME_COLOR_PRESET, customHex: '#1e6b58' }
}

export function saveResumeColorPreference(pref: ResumeColorPreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, pref.presetId)
    localStorage.setItem(STORAGE_CUSTOM_HEX_KEY, pref.customHex)
  } catch {
    /* ignore */
  }
}

export interface ResolvedResumeColors {
  accentHex: string
  nameHex: string
  sectionHeaderHex: string
  accentRgb: [number, number, number]
}

function normalizeHex(hex: string): string {
  const trimmed = hex.trim()
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) return trimmed.toLowerCase()
  if (/^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    const h = trimmed.slice(1)
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase()
  }
  return DEFAULT_RESUME_COLOR_PRESET === 'evergreen' ? '#00786c' : '#f57601'
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = normalizeHex(hex).slice(1)
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ]
}

export function resolveResumeColors(pref: ResumeColorPreference): ResolvedResumeColors {
  let accentHex: string
  if (pref.presetId === 'custom') {
    accentHex = normalizeHex(pref.customHex)
  } else {
    const preset = RESUME_COLOR_PRESETS.find((p) => p.id === pref.presetId)
    accentHex = preset?.accentHex ?? RESUME_COLOR_PRESETS[1].accentHex
  }
  return {
    accentHex,
    nameHex: accentHex,
    sectionHeaderHex: accentHex,
    accentRgb: hexToRgb(accentHex),
  }
}

/** Left-panel section types whose preview block uses accent section headers. */
export const RESUME_ACCENT_SECTION_TYPES = new Set([
  'work_experience',
  'education',
  'skills',
  'interests',
  'certifications',
  'awards_scholarships',
  'projects',
])
